import React, { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Target, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import productsData from "../data/products.json";

const products = Object.values(productsData).map(product => ({
  ...product,
  color: "from-brand-yellow to-brand-gold",
  bgColor: "bg-brand-yellow/10"
}));

const Products = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <section
      className="relative py-24 md:py-32 bg-gradient-to-br from-white via-gray-50 to-gray-100 overflow-hidden scroll-mt-20"
      id="products"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-brand-yellow/10 to-transparent rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-brand-navy/5 to-transparent rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-gradient-to-r from-brand-yellow/8 to-transparent rounded-full blur-2xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-l from-white/60 to-transparent rounded-full blur-2xl animate-pulse" />
      </div>

      <div className="container relative z-10">
        {/* Header Section */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="text-3xl font-semibold mb-6 uppercase tracking-wider"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Our Products
          </motion.div>

          <motion.h2
            className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            Premium Agricultural
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-yellow to-brand-gold">
              Products
            </span>
          </motion.h2>

          <motion.p
            className="text-xl md:text-2xl text-gray-600 max-w-5xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            Exporting premium beans, pulses, oil seeds, and spices to global
            markets across USA, Europe, Asia, Africa, and North America.
          </motion.p>
        </motion.div>

        {/* Products Grid */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          {products.map((product, index) => {
            const isHovered = hoveredCard === index;

            return (
              <motion.div
                key={index}
                className="group relative"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                onHoverStart={() => setHoveredCard(index)}
                onHoverEnd={() => setHoveredCard(null)}
              >
                <motion.div
                  className={`relative rounded-3xl overflow-hidden transition-all duration-500 ${
                    isHovered ? "scale-105 shadow-2xl" : "shadow-lg"
                  }`}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Product Image */}
                  <img
                    src={product.image}
                    alt={product.title}
                    loading="lazy"
                    className="w-full h-[28rem] object-cover"
                  />

                  {/* Content Overlay with Blur */}
                  <div className="absolute bottom-0 left-0 right-0 backdrop-blur-md bg-black/30 p-6">
                    <h3 className="text-xl font-bold mb-2 text-white">
                      {product.title}
                    </h3>

                    <p className="text-white text-sm leading-relaxed mb-4">
                      {product.description}
                    </p>

                    {/* Details Button */}
                    <Link to={`/product-details/${product.slug}`}>
                      <motion.button
                        className="w-full px-6 py-3 bg-gradient-to-r from-brand-yellow to-brand-gold text-brand-navy rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        View Details
                        <ArrowRight className="w-4 h-4" />
                      </motion.button>
                    </Link>
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* View All Products Button */}
        <motion.div
          className="flex justify-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <Link to="/all-products">
            <motion.button
              className="px-8 py-3 bg-white border border-gray-200 text-gray-900 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 shadow-sm hover:shadow-md"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              View All Products
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Products;
